import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { 
  Container, 
  Box, 
  Typography, 
  Button, 
  Alert, 
  Paper, 
  CircularProgress 
} from '@mui/material';
import { DEFAULT_THEME, type ThemeColors } from '../../constants/themeDefaults';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

interface PaymentFormProps {
  priceId?: string;
}

const PaymentForm: React.FC<PaymentFormProps> = ({ priceId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const theme = DEFAULT_THEME;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements || isSubmitted) {
      return;
    }

    setIsProcessing(true);
    setIsSubmitted(true);
    setPaymentError('');

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setPaymentError(submitError.message || 'An error occurred');
        setIsSubmitted(false);
        return;
      }

      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const token = localStorage.getItem('token');

      if (!token) {
        setPaymentError('Authentication required');
        setIsSubmitted(false);
        return;
      }

      const params = new URLSearchParams(location.search);
      const email = params.get('email');
      const password = params.get('password');

      if (email && password) {
        localStorage.setItem('tempAuthEmail', email);
        localStorage.setItem('tempAuthPassword', password);
      }

      const response = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/payment-success`,
          payment_method_data: {
            billing_details: {
              name: `${user.firstName} ${user.lastName}`,
              email: user.email
            }
          }
        },
      });

      if (response.error) {
        setPaymentError(response.error.message || 'An error occurred during payment setup');
        setIsSubmitted(false);
      } else if ('setupIntent' in response && response.setupIntent && 
                ((response.setupIntent as any).status === 'succeeded' || 
                 (response.setupIntent as any).status === 'processing')) {
        
        const paymentIntentId = response.setupIntent 
          ? (response.setupIntent as any).payment_intent || 'unknown'
          : 'unknown';
        
        navigate(`/payment-success?payment_intent=${paymentIntentId}`);
      }
    } catch (error) {
      setPaymentError('An unexpected error occurred. Please try again.');
      console.error('Payment error:', error);
      setIsSubmitted(false);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        bgcolor: theme.backgroundColor,
        py: 4
      }}
    >
      <Container maxWidth="md">
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            display: 'flex', 
            flexDirection: 'column', 
            borderRadius: 2,
            backgroundColor: theme.backgroundColor
          }}
        >
          <Box sx={{ mb: 5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CalendarMonthIcon sx={{ color: theme.navigationColor, mr: 1, fontSize: '2rem' }} />
            <Typography variant="h4" fontWeight="700" color={theme.textColor}>
              MindShare
            </Typography>
          </Box>

          <Typography variant="h4" fontWeight="700" gutterBottom color={theme.textColor} align="center">
            Complete Your Subscription
          </Typography>
          
          <Typography variant="body1" color="text.secondary" mb={4} align="center">
            Your account is almost ready. Please add your payment details below to start your 7 day free trial.
          </Typography>
          
          {paymentSuccess && (
            <Alert severity="success" sx={{ mb: 3 }}>
              Payment successful! Redirecting to your dashboard...
            </Alert>
          )}
          
          {paymentError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {paymentError}
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Box sx={{ mb: 3 }}>
              <PaymentElement 
                options={{
                  layout: { type: 'tabs', spacedAccordionItems: false },
                  fields: {
                    billingDetails: {
                      name: 'never',
                      email: 'never',
                      phone: 'auto',
                      address: {
                        country: 'auto',
                        line1: 'auto',
                        line2: 'auto',
                        city: 'auto',
                        state: 'auto',
                        postalCode: 'auto'
                      }
                    }
                  },
                  paymentMethodOrder: ['card'],
                  defaultValues: {
                    billingDetails: {
                      address: {
                        country: 'NZ'
                      }
                    }
                  },
                  wallets: {
                    applePay: 'never',
                    googlePay: 'never'
                  }
                }}
              />
            </Box>

            <Typography variant="caption" color="text.secondary" display="block" align="left" mt={3}>
              Your card will not be charged until the 7 day trial ends.
            </Typography>
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={!stripe || isProcessing}
              sx={{
                py: 1.5,
                mt: 3,
                backgroundColor: theme.navigationColor,
                color: theme.secondaryColor,
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem',
                '&:hover': {
                  backgroundColor: `${theme.navigationColor}dd`,
                },
              }}
            >
              {isProcessing ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Start 7 day free trial'}
            </Button>
            
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 3, mb: 1 }}>
              <img 
                src={process.env.PUBLIC_URL + '/images/payments/powered-by-stripe-black.svg'}
                alt="Powered by Stripe" 
                style={{ height: '24px' }} 
              />
            </Box>

            <Typography variant="caption" color="text.secondary" display="block" align="center">
              Your payment is processed securely through Stripe. We don't store your card details.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default PaymentForm; 